import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useLocation } from "react-router-dom";
import { apiJson } from "./api";

export type AdminEventRow = Record<string, unknown>;

export type AdminEventsDataContextValue = {
  rows: AdminEventRow[];
  loading: boolean;
  fetchError: string | null;
  reload: () => Promise<void>;
  setRows: Dispatch<SetStateAction<AdminEventRow[]>>;
};

const AdminEventsDataContext = createContext<AdminEventsDataContextValue | null>(null);

export function useAdminEventsData(): AdminEventsDataContextValue {
  const v = useContext(AdminEventsDataContext);
  if (!v) throw new Error("useAdminEventsData は AdminEventsDataProvider の内側でのみ使えます");
  return v;
}

/** `/api/admin/events` を1回まとめて取得し、予定一覧・月カレンダーで共有する */
export function AdminEventsDataProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [rows, setRows] = useState<AdminEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++seqRef.current;
    setFetchError(null);
    try {
      const data = await apiJson<{ events: AdminEventRow[] }>("/api/admin/events");
      if (seq !== seqRef.current) return;
      setRows(Array.isArray(data.events) ? data.events : []);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setFetchError(String(e));
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload, location.pathname]);

  const value = useMemo<AdminEventsDataContextValue>(
    () => ({ rows, loading, fetchError, reload, setRows }),
    [rows, loading, fetchError, reload, setRows],
  );

  return <AdminEventsDataContext.Provider value={value}>{children}</AdminEventsDataContext.Provider>;
}
